// src/api/wind-tunnel.ts
//
// Admin tools → Wind tunnel: the pre-deployment content test. A question
// battery is asked on the same models as control (question alone) and as one
// injection arm per draft (up to four drafts, each placed in front of the
// question); every answer is judged by the platform judge, the drafts are
// compared side by side, and each is checked against do-no-harm gates.
//
// Two doors in — an onboarded brand's approved framework, or pasted questions
// and variables — and one runner. Lanes, judges and gate defaults come DOWN
// with /options so nothing here duplicates the backend catalog.
import { apiFetch } from "./api-fetch";
import { downloadExportFile } from "./onboarding-api";

export type WindTunnelArm = "control" | "injection" | "retrieval";
export type WindTunnelStatus = "pending" | "running" | "completed" | "cancelled" | "failed";
export type WindTunnelVerdict = "PASS" | "CONDITIONAL" | "FAIL";
export type GateStatus = "OK" | "WARN" | "FAIL" | null;

export interface WindTunnelLaneOption {
  lane: string;
  label: string;
  role: "chat" | "retrieval";
  default_selected: boolean;
  web_search: boolean;
  per_call_cost_usd: number;
  choices: { value: string; label: string }[];
  defaults?: Record<string, string | null>;
  endpointDefault?: string;
  endpointChoices?: Record<string, { value: string; label: string }[]>;
}

export interface WindTunnelJudgeOption {
  id: string;
  label: string;
  provider: string;
  is_default?: boolean;
  recommended?: boolean;
}

export interface WindTunnelBrandOption {
  customer_id: number;
  brand_name: string;
  framework_approved: boolean;
  questions: number;
  variables: number;
}

export interface WindTunnelGates {
  variable_fail_drop: number;
  variable_warn_drop: number;
  mention_min_lift_pp: number;
  mention_fail_drop_pp: number;
}

export interface WindTunnelOptions {
  lanes: WindTunnelLaneOption[];
  judges: WindTunnelJudgeOption[];
  brands: WindTunnelBrandOption[];
  defaults: { passes: number; max_passes: number; gates: WindTunnelGates; concurrency: number; max_drafts: number };
}

export interface WindTunnelVariable {
  id: string;
  label: string | null;
  claim: string;
  score_2: string;
  score_1: string;
  score_0: string;
}

export interface WindTunnelQuery {
  id: string;
  text: string;
  category?: string | null;
}

export interface WindTunnelInputs {
  brand_name: string | null;
  name_variants: string[];
  variables: WindTunnelVariable[];
  queries: WindTunnelQuery[];
  warnings?: string[];
  customer_id?: number;
  battery_size?: number;
  framework_approved_at?: string | null;
  defaults?: { versions: Record<string, string>; scorer_model_id: string };
}

export interface WindTunnelEstimate {
  calls: number;
  probe_usd: number;
  judge_usd: number;
  total_usd: number;
  suggested_cap_usd: number;
}

export interface MentionStats {
  n: number;
  mentioned: number;
  rate: number | null;
}

export interface WindTunnelVariableRow {
  id: string;
  label: string;
  claim: string;
  control_avg: number | null;
  injection_avg: number | null;
  delta: number | null;
  control_n: number;
  injection_n: number;
  ci95: [number, number] | null;
  ci_half_width: number | null;
  status: GateStatus;
  distribution: Record<"control" | "injection", Record<"0" | "1" | "2", number>>;
  by_lane: Record<string, { control_avg: number | null; injection_avg: number | null; delta: number | null }>;
}

export interface WindTunnelGateRow {
  id: "mention_rate" | "variables";
  label: string;
  status: GateStatus;
  control_rate?: number | null;
  injection_rate?: number | null;
  threshold_rate?: number | null;
  delta_pp?: number | null;
  fail_variables?: string[];
  warn_variables?: string[];
}

export interface WindTunnelRetrievalRow extends MentionStats {
  lane: string;
  prior_read: {
    run_id: number;
    run_sequence_number: number;
    rate: number;
    n: number;
    same_questions: boolean;
  } | null;
  delta_pp_vs_read: number | null;
  parent: { run_id: number; rate: number; n: number } | null;
  delta_pp_vs_parent: number | null;
}

export type ShadeBand = "gain" | "gain_strong" | "loss" | "loss_strong" | null;

export interface WindTunnelTableCell {
  value: number | null;
  /** Difference from control: 0–2 mean for a belief, points for a rate. */
  delta: number | null;
  n: number;
  status: GateStatus;
  shade: ShadeBand;
  ci95?: [number, number] | null;
}

export interface WindTunnelTableRow {
  kind: "belief" | "visibility";
  id: string;
  label: string;
  claim: string;
  scale: "mean" | "rate";
  control: { value: number | null; n: number };
  drafts: Record<string, WindTunnelTableCell>;
  by_lane: Record<string, { control: number | null } & Record<string, { value: number | null; delta: number | null } | number | null>>;
}

export interface WindTunnelTable {
  columns: { id: string; label: string }[];
  rows: WindTunnelTableRow[];
  shade_lines: { mean: [number, number]; rate_pp: [number, number] };
}

export interface WindTunnelArmCost {
  calls_collected?: number;
  probe_usd: number;
  judge_usd: number;
  total_usd: number;
}

export interface WindTunnelDraftReport {
  id: string;
  label: string;
  sha256: string | null;
  chars: number;
  text: string;
  added_at: string | null;
  added_by: string | null;
  verdict: WindTunnelVerdict | null;
  /** The verdict worded as a read of this draft's column. */
  read: string;
  gates: WindTunnelGateRow[];
  variables: WindTunnelVariableRow[];
  cost: WindTunnelArmCost;
}

export interface WindTunnelReport {
  report_version: number;
  run_id: number;
  status: WindTunnelStatus;
  stop_reason: string | null;
  complete: boolean;
  /** Set only when the run has a single draft; otherwise read drafts[].verdict. */
  verdict: WindTunnelVerdict | null;
  verdict_rule: string;
  gated: boolean;
  table: WindTunnelTable | null;
  drafts: WindTunnelDraftReport[];
  thresholds: WindTunnelGates;
  control_cost: WindTunnelArmCost;
  retrieval: WindTunnelRetrievalRow[];
  reference: {
    label: string;
    draft_label?: string;
    reference_verdict: string | null;
    verdict_match: boolean | null;
    all_within_noise: boolean | null;
    matched_variables: number;
    variables: {
      id: string;
      label: string;
      reference_delta: number | null;
      delta: number | null;
      difference: number | null;
      tolerance: number | null;
      within_noise: boolean | null;
    }[];
  } | null;
  counts: {
    calls_total: number;
    calls_collected: number;
    calls_errored: number;
    calls_not_attempted: number;
    scored: number;
    failed_zeroed: number;
    excluded_no_answer: number;
  };
  cost: { probe_usd: number; judge_usd: number; total_usd: number; spend_cap_usd: number | null };
  config: {
    arms: WindTunnelArm[];
    lanes: { lane: string; label: string; role: string; model_version: string | null; web_search: boolean }[];
    passes: number;
    judge: { scorer_model_id?: string; model_string?: string };
    injection_mode: string | null;
    keys: Record<string, string>;
    questions: number;
    variables: number;
    work_scope?: { draft_ids: string[] } | null;
  };
}

export interface WindTunnelDraftVerdict {
  id: string;
  label: string;
  verdict: WindTunnelVerdict | null;
}

export interface WindTunnelDraft {
  id: string;
  label: string;
  text: string;
  sha256: string;
  added_at: string | null;
  added_by: string | null;
  calls?: number;
  spend_cap_usd?: number;
}

export interface WindTunnelRun {
  id: number;
  name: string | null;
  source: "brand" | "adhoc";
  customer_id: number | null;
  brand_name: string;
  parent_run_id: number | null;
  status: WindTunnelStatus;
  stop_reason: string | null;
  cancel_requested: boolean;
  calls_total: number;
  calls_completed: number;
  calls_scored: number;
  probe_cost_usd: number;
  judge_cost_usd: number;
  verdict: WindTunnelVerdict | null;
  draft_verdicts?: WindTunnelDraftVerdict[] | null;
  error_message: string | null;
  created_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  stale: boolean;
  config_json: {
    arms: WindTunnelArm[];
    passes: number;
    spend_cap_usd: number;
    estimate?: WindTunnelEstimate;
    lanes: { lane: string; label: string; role: string; model_version: string | null }[];
  };
  content_text?: string;
  drafts_json?: WindTunnelDraft[] | null;
  inputs_json?: WindTunnelInputs;
  report_json?: WindTunnelReport | null;
}

export interface WindTunnelLaneChoice {
  lane: string;
  model_version?: string | null;
}

export interface WindTunnelLaunch {
  source: "brand" | "adhoc";
  name?: string;
  customer_id?: number;
  query_ids?: string[];
  brand_name?: string;
  questions_text?: string;
  variables_text?: string;
  name_variants_text?: string;
  drafts: { label?: string; text: string }[];
  lanes: WindTunnelLaneChoice[];
  arms: WindTunnelArm[];
  passes: number;
  scorer_model_id?: string;
  spend_cap_usd: number;
  gates?: Partial<WindTunnelGates>;
  reference?: unknown;
  reference_label?: string;
}

const BASE = "/admin/wind-tunnel";

export const windTunnelApi = {
  options: () => apiFetch<WindTunnelOptions>(`${BASE}/options`),

  brandInputs: (customerId: number) =>
    apiFetch<WindTunnelInputs>(`${BASE}/brands/${customerId}/inputs`),

  parse: (body: { questions_text: string; variables_text: string; name_variants_text: string }) =>
    apiFetch<WindTunnelInputs>(`${BASE}/parse`, { method: "POST", body: JSON.stringify(body) }),

  estimate: (body: {
    lanes: WindTunnelLaneChoice[];
    arms: WindTunnelArm[];
    questions: number;
    passes: number;
    scorer_model_id?: string;
    drafts?: number;
  }) => apiFetch<WindTunnelEstimate>(`${BASE}/estimate`, { method: "POST", body: JSON.stringify(body) }),

  list: () => apiFetch<{ runs: WindTunnelRun[] }>(`${BASE}/runs`),

  get: (runId: number) => apiFetch<WindTunnelRun>(`${BASE}/runs/${runId}`),

  launch: (body: WindTunnelLaunch) =>
    apiFetch<WindTunnelRun>(`${BASE}/runs`, { method: "POST", body: JSON.stringify(body) }),

  cancel: (runId: number, cancel: boolean) =>
    apiFetch<WindTunnelRun>(`${BASE}/runs/${runId}/cancel`, {
      method: "POST",
      body: JSON.stringify({ cancel }),
    }),

  resume: (runId: number, spendCapUsd?: number) =>
    apiFetch<WindTunnelRun>(`${BASE}/runs/${runId}/resume`, {
      method: "POST",
      body: JSON.stringify({ spend_cap_usd: spendCapUsd ?? null }),
    }),

  /** Append another draft arm to a completed run; control is not re-run. */
  addDraft: (runId: number, body: { label?: string; text: string; spend_cap_usd: number }) =>
    apiFetch<WindTunnelRun>(`${BASE}/runs/${runId}/drafts`, { method: "POST", body: JSON.stringify(body) }),

  recheck: (runId: number, spendCapUsd: number) =>
    apiFetch<WindTunnelRun>(`${BASE}/runs/${runId}/recheck`, {
      method: "POST",
      body: JSON.stringify({ spend_cap_usd: spendCapUsd }),
    }),

  download: (runId: number, format: "txt" | "json" | "csv") =>
    downloadExportFile(
      `${BASE}/runs/${runId}/export?format=${format}`,
      `wind_tunnel_${runId}.${format}`,
    ),
};
